import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import LessonOptionStatusChip from '../../components/host/LessonOptionStatusChip';
import PassEligibleTutorsRow from '../../components/passes/PassEligibleTutorsRow';
import RelatedPassRow from '../../components/passes/RelatedPassRow';
import TutorLessonSessionTypeChip from '../../components/tutor/TutorLessonSessionTypeChip';
import { LessonOptionDetailResponse } from '../../models/LessonOption';
import { OrganizationType } from '../../models/Organization';
import { formatPassMoney, formatPassValidUntil } from '../../utils/passFormatting';
import type { PassCreateFromLessonLocationState } from '../../utils/passDuplicate';

type LessonLocationState = {
  flashMessage?: string;
  flashSeverity?: 'success' | 'error';
};

const pageSx = {
  p: { xs: 2, md: 4 },
  pb: { xs: 8, md: 10 },
  maxWidth: 800,
  width: '100%',
  mx: 'auto',
} as const;

const HostLessonOptionDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [option, setOption] = useState<LessonOptionDetailResponse | null>(null);
  const [isBusinessOrg, setIsBusinessOrg] = useState(false);
  const [isIndividualTutorOrg, setIsIndividualTutorOrg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const state = location.state as LessonLocationState | null;
    if (state?.flashMessage) {
      setSnackbar({
        open: true,
        message: state.flashMessage,
        severity: state.flashSeverity === 'error' ? 'error' : 'success',
      });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const fetchOption = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [data, organization] = await Promise.all([
        hostApiClient.getLessonOption(id),
        hostApiClient.getOrganization().catch(() => null),
      ]);
      setOption(data);
      setIsBusinessOrg(
        organization?.organizationType != null &&
          organization.organizationType !== OrganizationType.INDIVIDUAL_TUTOR,
      );
      setIsIndividualTutorOrg(organization?.organizationType === OrganizationType.INDIVIDUAL_TUTOR);
    } catch (err) {
      console.error(`Error fetching host lesson option ${id}:`, err);
      setError(t('hostLessonOptions.fetchDetailError', 'Failed to load this lesson option'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchOption();
  }, [fetchOption]);

  const handleCreatePass = () => {
    if (!option) return;
    navigate('/host/passes/new', {
      state: {
        prefillTarget: {
          targetType: 'LESSON_OPTION',
          targetIds: [option.id],
          currencyCode: option.currencyCode,
        },
        returnToLessonOptionId: option.id,
      } satisfies PassCreateFromLessonLocationState,
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !option) {
    return (
      <Box sx={pageSx}>
        <Chip
          label={`← ${t('hostLessonOptions.backToCatalog', 'Back to lessons')}`}
          onClick={() => navigate('/host/lesson-options')}
          sx={{
            mb: 2.5,
            bgcolor: 'transparent',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'transparent' },
          }}
        />
        <Alert severity="error">
          {error || t('hostLessonOptions.fetchDetailError', 'Failed to load this lesson option')}
        </Alert>
      </Box>
    );
  }

  const isArchived = option.status === 'ARCHIVED';
  const assignedTutors = option.assignedTutors ?? [];
  const relatedPasses = option.relatedPasses ?? [];
  const editPath = `/host/lesson-options/${option.id}/edit`;
  const description = option.description?.trim() || '';
  const createdLabel = formatPassValidUntil(option.createdAt, i18n.language);
  const updatedLabel = formatPassValidUntil(option.updatedAt, i18n.language);
  const metaItems = [
    {
      label: t('hostLessonOptions.details.created', 'Created'),
      value: createdLabel || '—',
    },
    ...(updatedLabel && updatedLabel !== createdLabel
      ? [{ label: t('hostLessonOptions.details.updatedLabel', 'Updated'), value: updatedLabel }]
      : []),
  ];

  return (
    <Box sx={pageSx}>
      <Chip
        label={`← ${t('hostLessonOptions.backToCatalog', 'Back to lessons')}`}
        onClick={() => navigate('/host/lesson-options')}
        sx={{
          mb: 2.5,
          bgcolor: 'transparent',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'transparent' },
        }}
      />

      {isArchived ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'hostLessonOptions.archivedReadOnlyHint',
            'This option is archived and can’t be edited. Manage it from the Archive tab.'
          )}
        </Alert>
      ) : null}

      <Card
        variant="outlined"
        sx={{
          position: 'relative',
          p: { xs: 2, sm: 2.5 },
          mb: 4,
          borderRadius: 2,
          borderColor: 'divider',
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            position: 'absolute',
            top: { xs: 10, sm: 14 },
            right: { xs: 10, sm: 14 },
            zIndex: 2,
          }}
        >
          <TutorLessonSessionTypeChip sessionType={option.sessionType} />
          <LessonOptionStatusChip status={option.status} />
          {!isArchived ? (
            <Tooltip title={t('common.edit', 'Edit')}>
              <IconButton
                size="small"
                aria-label={t('common.edit', 'Edit')}
                onClick={() => navigate(editPath)}
                sx={{
                  ml: 0.25,
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>

        <Stack spacing={2}>
          <Stack spacing={0.75} sx={{ minWidth: 0, pr: { xs: 18, sm: 22 } }}>
            <Typography
              variant="h4"
              component="h1"
              fontWeight={800}
              sx={{
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.35rem', sm: '1.6rem' },
              }}
            >
              {option.name}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
              <Stack direction="row" spacing={0.5} alignItems="baseline">
                <PaymentsRoundedIcon sx={{ fontSize: 18, color: 'text.secondary', alignSelf: 'center' }} />
                <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  {formatPassMoney(option.price, option.currencyCode || 'PLN', i18n.language)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('hostLessonOptions.details.perLesson', 'per lesson')}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <AccessTimeRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('hostLessonOptions.durationMinutes', '{{count}} min', { count: option.durationMinutes })}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {description ? (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          ) : null}

          {isIndividualTutorOrg ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ pt: { xs: 1.5, sm: 2 }, mt: 0.5, pb: 0.5 }}
            >
              {metaItems.map((item) => (
                <Box key={item.label}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block' }}
                  >
                    {item.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                columnGap: 2,
                rowGap: 0.75,
                alignItems: 'end',
                pt: { xs: 1.5, sm: 2 },
                mt: 0.5,
                pb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                {t('hostLessonOptions.details.assignedTutors', 'Assigned tutors')}
              </Typography>
              <Stack
                direction="row"
                spacing={2.5}
                useFlexGap
                flexWrap="wrap"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  justifyContent: 'flex-end',
                }}
              >
                {metaItems.map((item) => (
                  <Typography
                    key={item.label}
                    variant="caption"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
                  >
                    {item.label}
                  </Typography>
                ))}
              </Stack>
              <Box sx={{ minWidth: 0 }}>
                {assignedTutors.length > 0 ? (
                  <PassEligibleTutorsRow tutors={assignedTutors} audience="host" heading="" />
                ) : (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.25}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 1.5,
                      border: '1px dashed',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        'hostLessonOptions.details.noTutors',
                        'No tutors assigned yet. Students won’t see this lesson until someone is assigned.'
                      )}
                    </Typography>
                    {!isArchived ? (
                      <Button size="small" onClick={() => navigate(editPath)} sx={{ flexShrink: 0 }}>
                        {t('hostLessonOptions.details.assignTutors', 'Assign tutors')}
                      </Button>
                    ) : null}
                  </Stack>
                )}
              </Box>
              <Stack spacing={0.75} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                <Stack
                  direction="row"
                  spacing={2.5}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ display: { xs: 'flex', sm: 'none' } }}
                >
                  {metaItems.map((item) => (
                    <Typography
                      key={item.label}
                      variant="caption"
                      color="text.secondary"
                      sx={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
                    >
                      {item.label}
                    </Typography>
                  ))}
                </Stack>
                <Stack direction="row" spacing={2.5} useFlexGap flexWrap="wrap">
                  {metaItems.map((item) => (
                    <Typography key={item.label} variant="body2" fontWeight={600}>
                      {item.value}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </Card>

      {isBusinessOrg || isIndividualTutorOrg ? (
        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.25 }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
              {t('hostLessonOptions.details.relatedPasses', 'Passes')}
            </Typography>
            {!isArchived ? (
              <Button size="small" startIcon={<AddIcon />} onClick={handleCreatePass}>
                {t('hostLessonOptions.details.createPass', 'Create pass')}
              </Button>
            ) : null}
          </Stack>
          {relatedPasses.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
              {t('hostLessonOptions.details.relatedPassesEmpty', 'No passes for this lesson yet.')}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 0.75,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
              }}
            >
              {relatedPasses.map((pass) => (
                <RelatedPassRow
                  key={pass.id}
                  pass={pass}
                  onClick={() => navigate(`/host/passes/${pass.id}`)}
                />
              ))}
            </Box>
          )}
        </Box>
      ) : null}

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default HostLessonOptionDetailsPage;
